<template>
    <q-dialog v-model="showModal" persistent>
        <q-card style="width: 500px; max-width: 80vw;">
            <q-card-section>
                <p class="text-h6">{{defaultAnswer === false ? $t('users.update_information') : $t('users.do_you_still_live_here')}}</p>
                <div class="text-italic">{{ address }}</div>
            </q-card-section>

            <q-card-section class="q-py-none" v-show="defaultAnswer !== false">
                <q-option-group
                    :options="options"
                    type="radio"
                    v-model="group"
                />
            </q-card-section>

            <q-card-section>
                <div class="row q-col-gutter-sm" v-if="group === true">
                    <div class="col-12">
                        <p class="prevent-select text-body1 text-weight-medium q-mt-md">{{ $t('type') }} 'NO CHANGE'</p>
                        <q-input
                            outlined
                            v-model="answer"
                            :placeholder="$t('type_here')"
                            input-class="text-uppercase"
                        />
                    </div>
                </div>

                <div class="row q-col-gutter-sm" v-else-if="group === false">
                    <div class="col-12">
                        <div class="prevent-select text-body1 text-weight-medium q-my-sm">{{$t('users.new_address')}}</div>
                        <q-skeleton v-if="loading" type="QInput" class="q-my-sm"/>
                        <q-select
                            outlined
                            :label="$t('users.table_titles.country')"
                            v-model="UserObj.country"
                            emit-value
                            map-options
                            option-value="acronym"
                            option-label="name"
                            use-input
                            :options="countryOptions"
                            :loading="loading"
                            :error="UserObj.errors.has('country')"
                            :error-message="UserObj.errors.get('country')"
                            :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.table_titles.country')})]"
                            color="primary"
                            @filter="filterCountries"
                            @input="setCountry"
                            dir="ltr"
                            v-else
                        >
                            <template v-slot:option="scope">
                                <q-item
                                    v-bind="scope.itemProps"
                                    v-on="scope.itemEvents"
                                >
                                    <q-item-section avatar>
                                        <q-icon :name="scope.opt.acronym" size="sm" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{scope.opt.name}}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-select>
                    </div> <!-- Country -->
                    <div class="col-12">
                        <q-skeleton type="QInput" v-if="loading" class="q-my-sm"/>
                        <q-input
                            outlined
                            v-model="UserObj.mobile"
                            :label="$t('mobile')"
                            mask="###########"
                            color="primary"
                            clearable
                            lazy-rules
                            :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('mobile')})]"
                            :error="UserObj.errors.has('mobile')"
                            :error-message="UserObj.errors.get('mobile')"
                            dir="ltr"
                            :class="{'fix-template': $q.lang.rtl}"
                            v-else
                        >
                            <template v-slot:prepend>
                                <small>+{{ selectedCountry.calling_code }}</small>
                            </template>
                        </q-input>
                    </div> <!-- Mobile -->
                    <div class="col-12">
                        <q-select
                            outlined
                            :label="$t('new_address')"
                            :value="UserObj.new_address"
                            use-input
                            hide-selected
                            fill-input
                            input-debounce="0"
                            :options="addresses"
                            @filter="filterAddresses"
                            @input-value="setAddress"
                            hide-dropdown-icon
                            :placeholder="isAddressSearchable ? $t('start_typing_new_address') : ''"
                            dir="ltr"
                        />
                    </div> <!-- New address -->
                    <div class="col-12">
                        <div class="prevent-select text-body1 text-weight-medium q-my-sm">{{$t('users.upload_new_address_proof')}}</div>
                        <file-collector
                            v-model="UserObj.documents"
                            multiple
                            class="col-12"
                            :label="$t('address_proof')"
                            name="documents"
                            color="grey-8"
                            :attach-file-label="$t('attach_file')"
                            ref="collector"
                            @count="fileCounts"
                            file-types="*"
                            :max-file-size="25 * 1024 * 1024"
                            :max-total-size="40 * 1024 * 1024"
                            :error="UserObj.errors.get('documents', true)"
                            camera
                            :cameraLabel="$t('take_photo')"
                            :of="$t('of')"
                        />
                    </div> <!-- Documents -->
                </div>
            </q-card-section>

            <q-card-actions class="flex justify-between">
                <q-btn flat :label="$t('cancel')" color="primary" v-close-popup/>
                <q-btn @click="submit" :label="$t('submit')" color="primary" :disabled="!permitToSubmit" :loading="saving"/>
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>
<script>

import User from "Modules/User/resources/models/User"
import FileCollector from "src/components/FileCollector.vue"
import {mapActions, mapGetters} from 'vuex'

export default {
    name: "AddressProofDialog",
    model: {
        prop: 'open',
        event: 'change'
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        address: {
            type: String,
            required: true
        },
        userId: {
            type: Number,
            required: true
        },
        country: {
            type: String,
            required: true
        },
        mobile: {
            type: String,
            required: true
        },
        defaultAnswer: {
            type: Boolean,
            default: null
        }
    },
    computed: {
        ...mapGetters(['id_valid_countries']),
        showModal: {
            get: function () {
                return this.open
            },
            set: function (value) {
                this.$emit('change', value)
            }
        },
        documents() {
            return this.UserObj.documents
        },
        permitToSubmit() {
            const condition1 = (this.group === true) && this.answer.toUpperCase().trim() === 'NO CHANGE'
            const condition2 = (this.group === false) && this.UserObj.new_address && this.count_of_files && this.UserObj.country && this.UserObj.mobile

            return condition1 || condition2
        },
        isAddressSearchable() {
            return ['NZ', 'AU'].includes(this.UserObj.country)
        },
    },
    data() {
        return {
            group: this.defaultAnswer,
            options: [
                {label: this.$t('users.yes_i_live_at', {address: this.address.substring(0, 20)}), value: true},
                {label: this.$t('users.no_that_s_my_old_address'), value: false}
            ],
            answer: '',
            loading: false,
            saving: false,
            count_of_files: 0,
            UserObj: new User({
                new_address: '',
                documents: {},
                country: undefined,
                mobile: this.mobile
            }),
            addressOptions: [],
            addresses: [],
            countryOptions: null,
            selectedCountry: null
        }
    },
    methods: {
        ...mapActions(['set_countries']),
        submit() {
            this.saving = true
            this.UserObj.updateAddressProof(({message}) => {
                this.saving = false
                this.count_of_files = 0
                this.$q.notify({
                    message: message
                })
                this.showModal = false
                this.$emit('updated', {
                    success: true,
                    address: this.UserObj.new_address,
                    country: this.UserObj.country,
                    mobile: this.UserObj.mobile,
                })
            }).catch(error => {
                this.saving = false
                switch (error.status) {
                    case 429:
                        this.$q.notify({
                            message: this.$t('errors.too_many_requests'),
                            caption: this.$t('please_try_again_another_day'),
                            color: 'negative',
                            icon: 'fa fa-exclamation-triangle'
                        })
                        break;
                    case 412:
                        this.$q.notify({
                            message: error.data.error,
                            color: 'negative',
                            icon: 'fa fa-exclamation-triangle',
                            timeout: 5000,
                        })

                }
                this.$emit('updated', {success: false})
            })
        },
        fileCounts(count) {
            this.count_of_files = count
        },
        filterAddresses(val, update, abort) {
            if (val.length < 5 || !this.selectedCountry || !['NZ', 'AU'].includes(this.selectedCountry.acronym)) {
                abort()
                return
            }

            User.addresses(`q=${val}`).then(response => {
                this.addressOptions = response
                update(() => {
                    this.addresses = this.addressOptions
                })
            }).catch(data => {
                abort()
            })
        },
        setAddress(address) {
            this.UserObj.new_address = address
        },
        filterCountries(val, update, abort) {
            if (val === '') {
                update(() => {
                    this.countryOptions = this.id_valid_countries
                })
                return
            }

            update(() => {
                const needle = val.toLowerCase()
                this.countryOptions = this.id_valid_countries.filter(country => country.name.toLowerCase().indexOf(needle) > -1)
            })
        },
        setCountry(acronym) {
            this.selectedCountry = this.id_valid_countries.find(country => country.acronym === acronym)
            this.UserObj.mobile = this.UserObj.country === this.country ? this.mobile : ''
        }
    },
    created() {
    },
    mounted() {
        if (!this.id_valid_countries.length) {
            this.loading = true
            this.set_countries().then(() => {
                this.loading = false
                this.UserObj.country = this.country
                this.setCountry(this.country)
            }).catch(() => {
                this.loading = false
            })
        } else {
            this.UserObj.country = this.country
            this.setCountry(this.country)
        }
    },
    watch: {
        group(val) {
            if (val) {
                this.UserObj.new_address = this.address
                this.UserObj.documents = {}
            } else {
                this.UserObj.new_address = this.new_address
                this.UserObj.mobile = this.mobile
                this.UserObj.country = this.country
            }
        },
        documents(val) {
            if (!val && this.group === false) {
                this.$refs.collector.reset()
            }
        },
    },
    components: {FileCollector},
}
</script>
<style>
.prevent-select {
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none; /* IE 10 and IE 11 */
    user-select: none; /* Standard syntax */
}
</style>
